import { type FormikHelpers } from 'formik'
import * as React from 'react'
import { toast } from 'react-toastify'
import * as Api from 'src/api'
import type * as base from 'src/api/base'
import { EmailIcon } from 'src/assets/icons/customIcons/Email'
import { errorMessage } from 'src/helpers/fns'
import { type AuthenticatedHeaders } from 'src/hooks/auth/app'
import { useTranslatable } from 'src/hooks/locale/utils'
import { Modal } from 'src/tailwind/components/Modal'
import { Form } from 'src/views/components/forms/formik/Form'
import MessageForm from '../../../../lecturer/courses/scores/MessageForm'

interface MessageValues {
  readonly subject: string
  readonly body: string
}

const MESSAGE_INITIAL_VALUES: MessageValues = { subject: '', body: '' }

interface Props {
  readonly headers: AuthenticatedHeaders
  readonly courseId: string
  readonly semId: number
  readonly groups: readonly Api.AdministratorCourseScoresGroup[]
  readonly recipientGroupIds: readonly number[]
  readonly isOpen: boolean
  readonly onClose: () => void
  readonly onSent: () => void
}

/** Messages every student of the picked groups at once — the admin twin of the lecturer page's group message. */
export default function GroupsMessageModal({
  headers,
  courseId,
  semId,
  groups,
  recipientGroupIds,
  isOpen,
  onClose,
  onSent,
}: Props): React.ReactElement {
  const t = useTranslatable()

  const recipientGroups = React.useMemo(
    () => groups.filter((group) => recipientGroupIds.includes(group.id)),
    [groups, recipientGroupIds]
  )

  const handleSend = React.useCallback(
    async (values: MessageValues, helpers: FormikHelpers<MessageValues>) => {
      try {
        await Api.postAdministratorCourseGroupsMessage({
          headers,
          args: { id: courseId },
          query: { semId },
          body: { groups: [...recipientGroupIds], subject: values.subject, body: values.body },
        })
        toast.success(t('uncategorized:message_successfully_sent'))
        helpers.resetForm()
        onSent()
      } catch (error) {
        toast.error(errorMessage(error as base.CommonError))
        throw error
      }
    },
    [courseId, headers, onSent, recipientGroupIds, semId, t]
  )

  return (
    <Modal title={t('message:messages_compose')} icon={<EmailIcon />} isModalOpen={isOpen} onClose={onClose}>
      {isOpen && (
        <Form
          initialValues={MESSAGE_INITIAL_VALUES}
          onSubmit={handleSend}
          classNames={{ form: 'overflow-auto py-4' }}
          isConfirmable
        >
          <MessageForm>
            {recipientGroups.map((group, key) => (
              <div className="mr-2" key={group.id}>
                {group.name}
                {key === recipientGroups.length - 1 ? '' : ','}
              </div>
            ))}
          </MessageForm>
        </Form>
      )}
    </Modal>
  )
}
